import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { coordinateDraftSave } from "@/lib/appointments/draft-create-coordinator";
import type {
	AdminPicSchema,
	AppointmentBookingSchema,
	DraftBaseline,
} from "@/lib/appointments/form";
import { getCsrfToken } from "@/lib/csrf";
import { USE_DRAFT_DATABASE } from "./use-appointment-form";

export const DRAFT_STEPS = [
	"patient_details",
	"appointment_scheduling",
	"additional_settings",
	"review",
] as const;

export const DRAFT_STEPS_LABELS = {
	patient_details: "Patient Details",
	appointment_scheduling: "Appointment Scheduling",
	additional_settings: "Additional Settings",
	review: "Review",
};

export const DRAFT_API_BASE_URL = "/api/v1/appointments";
const QUEUE_CODE_ENDPOINT = `${DRAFT_API_BASE_URL}/queue_code`;

interface DraftInfo {
	id: string;
	adminPic?: AdminPicSchema;
	// Both nullable for a Draft the customer created: nobody here authored it, and
	// fisiohome-backend inserts the row without an expiry. Kept in step with
	// AppointmentDraft in @/types/admin-portal/appointment-draft — the two describe the
	// same serializer payload, and tsc cannot catch them drifting apart.
	createdByAdmin: AdminPicSchema | null;
	fromCustomer: boolean;
	currentStep: string;
	stepIndex: number;
	nextStep: string | null;
	expiresAt: string | null;
	createdAt: string;
	updatedAt: string;
	admins?: Array<AdminPicSchema & { isPrimary: boolean }>;
}

interface UseAppointmentDraftProps {
	onDraftSaved?: (draft: DraftInfo) => void;
	onDraftLoaded?: (
		formData: AppointmentBookingSchema,
		currentStep?: string,
		draftBaseline?: DraftBaseline | null,
	) => void;
	onError?: (error: string) => void;
	// New props for URL draft loading
	draftIdFromUrl?: string | null;
	setFormStorage?: (data: any) => void;
	setDraftLoaded?: (loaded: boolean) => void;
}

export type DraftStep = (typeof DRAFT_STEPS)[number];

export const useAppointmentDraft = ({
	onDraftSaved,
	onDraftLoaded,
	onError,
	draftIdFromUrl,
	setFormStorage,
	setDraftLoaded,
}: UseAppointmentDraftProps = {}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [drafts, setDrafts] = useState<DraftInfo[]>([]);
	const [currentDraft, setCurrentDraft] = useState<DraftInfo | null>(null);
	const [internalDraftLoaded, setInternalDraftLoaded] = useState(false);

	const requestQueueCode = useCallback(async () => {
		try {
			const response = await fetch(QUEUE_CODE_ENDPOINT, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
			});

			const payload = await response.json().catch(() => ({}));

			if (!response.ok || payload.success === false) {
				const errorMessage = payload.error || "Failed to reserve queue code";
				return { success: false, error: errorMessage };
			}

			return {
				success: true,
				data: payload?.data || {},
				message: payload.message,
			};
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to reserve queue code";
			return { success: false, error: errorMessage };
		}
	}, []);

	const saveDraft = useCallback(
		async ({
			adminPicId,
			adminIds,
			primaryAdminId,
			currentStep,
			formData,
			draftId,
			signal,
		}: {
			adminPicId?: string;
			adminIds?: string[];
			primaryAdminId?: string;
			currentStep?: string;
			formData: any;
			draftId?: string;
			signal?: AbortSignal;
		}) => {
			setIsLoading(true);

			try {
				// Get CSRF token for security
				const csrfToken = getCsrfToken();

				// Filter out invalid admin IDs (0, undefined, null, or empty string)
				const validAdminIds = adminIds
					? adminIds.filter((id) => id && id !== "0")
					: [];

				// Also validate adminPicId and primaryAdminId
				const validAdminPicId =
					adminPicId && adminPicId !== "0" ? adminPicId : undefined;
				const validPrimaryAdminId =
					primaryAdminId && primaryAdminId !== "0" ? primaryAdminId : undefined;

				// Validate that at least one admin is selected. L1 counts: the multi-select
				// now holds only the supporting PICs, so emptying it is an ordinary thing to
				// do, and refusing to save a draft whose slots are filled would be a dead
				// end reported against the wrong field.
				const picL1 = formData?.additionalSettings?.picL1;
				if (!validAdminPicId && validAdminIds.length === 0 && !picL1) {
					throw new Error("At least one admin must be selected");
				}

				// Perform the save through the create-coordinator so concurrent saves
				// from the other paths (autosave, step-save, series handlers) don't each
				// POST a duplicate draft. `resolvedDraftId` is the id of an in-flight
				// create we waited on; combined with our own `draftId` it decides
				// POST-vs-PUT. See lib/appointments/draft-create-coordinator.
				const draft = await coordinateDraftSave(
					Boolean(draftId),
					async (resolvedDraftId) => {
						const effectiveDraftId = draftId ?? resolvedDraftId;
						const url = effectiveDraftId
							? `${DRAFT_API_BASE_URL}/drafts/${effectiveDraftId}`
							: `${DRAFT_API_BASE_URL}/drafts`;
						const method = effectiveDraftId ? "PUT" : "POST";

						const requestBody = {
							draft: {
								id: effectiveDraftId,
								admin_pic_id: validAdminPicId,
								// Always sent, empty included. The server reads a missing key as
								// "this caller does not manage the supporting PICs" and leaves them
								// alone, so omitting an emptied list would make clearing it
								// impossible. Harmless while the flag is off, where an empty array
								// falls through the same branch undefined did.
								admin_ids: validAdminIds,
								primary_admin_id: validPrimaryAdminId,
								current_step: currentStep,
								form_data: formData,
							},
						};

						const response = await fetch(url, {
							method,
							headers: {
								"Content-Type": "application/json",
								Accept: "application/json",
								"X-CSRF-Token": csrfToken,
							},
							body: JSON.stringify(requestBody),
							signal,
						});

						const data = await response.json();
						if (!data.success) {
							const errorMsg =
								data.errors?.join(", ") || "Failed to save draft";
							throw new Error(errorMsg);
						}
						return data.draft;
					},
				);

				setCurrentDraft(draft);
				onDraftSaved?.(draft);
				return { success: true, draft };
			} catch (error) {
				// An intentional abort (a newer autosave superseded this one) is not
				// a failure — don't surface it as an error.
				if (error instanceof Error && error.name === "AbortError") {
					return { success: false, aborted: true };
				}
				// Handle any errors that occurred during the save process
				const errorMsg =
					error instanceof Error ? error.message : "Failed to save draft";
				// Here, not in each caller: every autosave path used to drop a refused save silently (#274).
				toast.error(`Draft not saved: ${errorMsg}`);
				onError?.(errorMsg);
				return { success: false, error: errorMsg };
			} finally {
				// Always set loading to false when done
				setIsLoading(false);
			}
		},
		[onDraftSaved, onError],
	);

	const loadDraft = useCallback(
		async (draftId: string) => {
			setIsLoading(true);

			try {
				// Fetch the draft from the API
				const response = await fetch(
					`${DRAFT_API_BASE_URL}/drafts/${draftId}`,
					{
						method: "GET",
						headers: {
							Accept: "application/json",
						},
					},
				);

				const data = await response.json();

				// Handle successful draft loading
				if (data.success) {
					setCurrentDraft(data.draft);

					// Call the success callback FIRST so originalDraftData is set before useFormReset runs
					// This is important because setDraftLoaded triggers useFormReset which needs originalDraftData
					onDraftLoaded?.(
						data.form_data,
						data.current_step,
						data.draft_baseline,
					);

					// If loading from URL, include draftId in form data and update states
					if (setFormStorage && draftIdFromUrl) {
						const formDataWithDraftId = {
							...data.form_data,
							formOptions: {
								...data.form_data.formOptions,
								draftId: draftIdFromUrl,
							},
						};
						setFormStorage(formDataWithDraftId);
						setDraftLoaded?.(true);
						setInternalDraftLoaded(true);
					}

					return {
						success: true,
						draft: data.draft,
						formData: data.form_data,
						currentStep: data.current_step,
						admins: data.admins,
						primaryAdminId: data.primary_admin_id,
					};
				}

				// If server returns an error, throw it to be caught by the catch block
				throw new Error(data.error || "Failed to load draft");
			} catch (error) {
				// Handle any errors that occurred during the load process
				const errorMsg =
					error instanceof Error ? error.message : "Failed to load draft";
				onError?.(errorMsg);

				// Ensure loading states are properly set even on error
				if (setDraftLoaded) {
					setDraftLoaded(true);
					setInternalDraftLoaded(true);
				}

				return { success: false, error: errorMsg };
			} finally {
				// Always set loading to false when done
				setIsLoading(false);
			}
		},
		[onDraftLoaded, onError, setFormStorage, draftIdFromUrl, setDraftLoaded],
	);

	const listDrafts = useCallback(
		async (filters?: { adminPicId?: string; createdById?: string }) => {
			setIsLoading(true);

			try {
				// Build query parameters if filters are provided
				const params = new URLSearchParams();
				if (filters?.adminPicId)
					params.append("admin_pic_id", filters.adminPicId);
				if (filters?.createdById)
					params.append("created_by_id", filters.createdById);

				const url = `${DRAFT_API_BASE_URL}/drafts${params.toString() ? `?${params.toString()}` : ""}`;

				// Fetch the list of drafts
				const response = await fetch(url, {
					method: "GET",
					headers: {
						Accept: "application/json",
					},
				});

				const data = await response.json();

				// Handle successful list retrieval
				if (data.success) {
					setDrafts(data.drafts);
					return { success: true, drafts: data.drafts };
				}

				// If server returns an error, throw it to be caught by the catch block
				throw new Error(data.error || "Failed to list drafts");
			} catch (error) {
				// Handle any errors that occurred during the list process
				const errorMsg =
					error instanceof Error ? error.message : "Failed to list drafts";
				onError?.(errorMsg);
				return { success: false, error: errorMsg };
			} finally {
				// Always set loading to false when done
				setIsLoading(false);
			}
		},
		[onError],
	);

	const deleteDraft = useCallback(
		async (draftId: string) => {
			setIsLoading(true);

			try {
				// Send delete request to the API
				const response = await fetch(
					`${DRAFT_API_BASE_URL}/drafts/${draftId}`,
					{
						method: "DELETE",
						headers: {
							Accept: "application/json",
							"X-CSRF-Token": getCsrfToken(),
						},
					},
				);

				const data = await response.json();

				// Handle successful deletion
				if (data.success) {
					// Clear the current draft if it's the one being deleted
					setCurrentDraft(null);
					// Remove the draft from the list
					setDrafts((prev) => prev.filter((d) => d.id !== draftId));
					return { success: true };
				}

				// If server returns an error, throw it to be caught by the catch block
				throw new Error(data.error || "Failed to delete draft");
			} catch (error) {
				// Handle any errors that occurred during the delete process
				const errorMsg =
					error instanceof Error ? error.message : "Failed to delete draft";
				onError?.(errorMsg);
				return { success: false, error: errorMsg };
			} finally {
				// Always set loading to false when done
				setIsLoading(false);
			}
		},
		[onError],
	);

	// Utility functions to convert between step indices and step names
	const getStepFromIndex = useCallback((index: number): DraftStep => {
		// Return the step name at the given index, or default to the first step
		return DRAFT_STEPS[index] || DRAFT_STEPS[0];
	}, []);

	const getIndexFromStep = useCallback((step: string): number => {
		// Find the index of the given step name, or default to 0
		const index = DRAFT_STEPS.indexOf(step as DraftStep);
		return index >= 0 ? index : 0;
	}, []);

	/**
	 * Effect to handle draft loading from URL
	 * - Only runs if draft database is enabled and URL draft loading props are provided
	 * - Checks for draftId in URL parameters
	 * - Loads draft if found, otherwise marks as loaded
	 */
	useEffect(() => {
		// Only use draft loading if configured to do so and we have the necessary props
		if (
			USE_DRAFT_DATABASE &&
			draftIdFromUrl !== undefined &&
			setFormStorage &&
			setDraftLoaded
		) {
			// Check if we haven't loaded it yet
			if (!internalDraftLoaded) {
				if (draftIdFromUrl) {
					// Load the draft from database
					loadDraft(draftIdFromUrl);
				} else {
					// No draft to load, mark as loaded
					setDraftLoaded(true);
					setInternalDraftLoaded(true);
				}
			}
		} else if (!USE_DRAFT_DATABASE && setDraftLoaded) {
			// If not using draft database, mark as loaded immediately
			setDraftLoaded(true);
			setInternalDraftLoaded(true);
		}
	}, [
		loadDraft,
		internalDraftLoaded,
		draftIdFromUrl,
		setFormStorage,
		setDraftLoaded,
	]);

	return {
		isLoading,
		drafts,
		currentDraft,
		saveDraft,
		loadDraft,
		listDrafts,
		deleteDraft,
		requestQueueCode,
		getStepFromIndex,
		getIndexFromStep,
		setCurrentDraft,
	};
};
